/**
 * Per-route document head.
 *
 * index.html carries one static <head>, and this is a single-page app — so
 * without this hook every URL shares the entry document's title, description
 * and canonical. That is wrong in three separate ways: the browser tab never
 * changes as you navigate, a pasted link previews as the generic product card
 * whatever it points at, and search engines see one page where there are
 * several.
 *
 * Two rules the call sites depend on:
 *
 *  1. Anything behind the login is `noindex`. Those routes redirect a signed-
 *     out visitor — and therefore every crawler — to /login, so an indexed
 *     entry could only ever be a dead end pointing at the sign-in screen.
 *  2. Anything reached with a one-time ?token= (/reset-password,
 *     /address-update) is `noindex, nofollow, noarchive`. Indexing one of
 *     those publishes the token itself.
 *
 * Canonical is derived from window.location rather than a configured hostname:
 * staging and production serve the same bundle, and a hard-coded host would
 * make staging claim to be production.
 */
import { useEffect } from 'react';

export const SITE_NAME = 'Nixook';

/** Appended to every page title; the brand is what makes a tab identifiable. */
const TITLE_SUFFIX = ` | ${SITE_NAME}`;

export interface SeoOptions {
  /** Page title WITHOUT the brand suffix, e.g. "Sign In". */
  title: string;
  description?: string;
  /**
   * Leave unset for private screens — the default is noindex, which is the
   * safe direction to be wrong in for an admin console.
   */
  index?: boolean;
  /** Drops the canonical tag as well; for one-time tokenised URLs. */
  sensitive?: boolean;
}

/** Create-or-update, so repeated navigation never stacks duplicate tags. */
function setMeta(selector: string, attr: 'name' | 'property', key: string, content: string) {
  let el = document.head.querySelector<HTMLMetaElement>(selector);
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute('content', content);
}

function setLink(rel: string, href: string | null) {
  let el = document.head.querySelector<HTMLLinkElement>(`link[rel="${rel}"]`);
  if (href === null) {
    el?.remove();
    return;
  }
  if (!el) {
    el = document.createElement('link');
    el.setAttribute('rel', rel);
    document.head.appendChild(el);
  }
  el.setAttribute('href', href);
}

export function useSeo({ title, description, index = false, sensitive = false }: SeoOptions) {
  useEffect(() => {
    const fullTitle = title.endsWith(SITE_NAME) ? title : `${title}${TITLE_SUFFIX}`;
    document.title = fullTitle;
    setMeta('meta[property="og:title"]', 'property', 'og:title', fullTitle);
    setMeta('meta[name="twitter:title"]', 'name', 'twitter:title', fullTitle);

    if (description) {
      setMeta('meta[name="description"]', 'name', 'description', description);
      setMeta('meta[property="og:description"]', 'property', 'og:description', description);
      setMeta('meta[name="twitter:description"]', 'name', 'twitter:description', description);
    }

    setMeta(
      'meta[name="robots"]',
      'name',
      'robots',
      sensitive
        ? 'noindex, nofollow, noarchive'
        : index
          ? 'index, follow, max-image-preview:large, max-snippet:-1'
          : 'noindex, nofollow',
    );

    // Query strings are what carry the tokens, so the canonical is always the
    // bare path — and is dropped entirely on a sensitive screen.
    const canonical = `${window.location.origin}${window.location.pathname}`;
    setLink('canonical', sensitive ? null : canonical);
    // og:url is never dropped, only redirected: these tags persist across
    // client-side navigation, so leaving the previous route's value in place
    // would have a tokenised page advertising the URL you arrived from.
    setMeta(
      'meta[property="og:url"]',
      'property',
      'og:url',
      sensitive ? `${window.location.origin}/` : canonical,
    );
  }, [title, description, index, sensitive]);
}
